<template>
  <div class="flex flex-col gap-6 py-3">
    <div class="grid grid-cols-2 gap-2">
      <div>
        <WalkmanButton
          v-model="localPerson.is_company"
          :label="$t('attributes.is_company')"
          :options="[
            { value: 1, label: $t('enums.yesNo.1') },
            { value: 0, label: $t('enums.yesNo.0') }
          ]"
        />
      </div>
      <template v-if="localPerson.is_company">
        <div class="col-start-1">
          <BaseShowEditInput
            v-model="localPerson.company_name"
            :label="$t('attributes.company_name')"
            name="name"
            edition-mode
            required
            :errors="errors?.company_name"
          />
        </div>
        <div>
          <BaseShowEditInput
            v-model="localPerson.tva"
            :label="$t('attributes.tva')"
            name="name"
            edition-mode
            required
            :errors="errors?.tva"
          />
        </div>
        <div>
          <BaseShowEditInput
            v-model="localPerson.business_number"
            :label="$t('attributes.business_number')"
            name="name"
            edition-mode
            :errors="errors?.business_number"
          />
        </div>
        <div>
          <BaseShowEditInput
            v-model="localPerson.gln_number"
            :label="$t('attributes.gln_number')"
            name="name"
            edition-mode
            :errors="errors?.gln_number"
          />
        </div>
        <div>
          <BaseShowEditInput
            v-if="localPerson.country === Country.FRANCE"
            v-model="localPerson.siret"
            :label="$t('attributes.siret')"
            name="siret"
            edition-mode
            :errors="errors?.siret"
          />
        </div>
      </template>
      <template v-else>
        <div class="col-start-1">
          <BaseShowEditRadio
            v-model="localPerson.gender"
            :label="$t('attributes.gender')"
            edition-mode
            name="gender"
            :options="metadata?.enums?.genders"
          />
        </div>
        <div class="col-start-1">
          <SelectCivility
            v-model="localPerson.civility"
            edition-mode
            :errors="errors?.civility"
          />
        </div>
        <div class="col-start-1">
          <BaseShowEditInput
            v-model="localPerson.last_name"
            :label="$t('attributes.name')"
            name="name"
            edition-mode
            required
            :errors="errors?.last_name"
          />
        </div>
        <div>
          <BaseShowEditInput
            v-model="localPerson.first_name"
            :label="$t('attributes.first_name')"
            name="first_name"
            edition-mode
            required
            :errors="errors?.first_name"
          />
        </div>
        <div v-if="localPerson.gender === Gender.FEMALE">
          <BaseShowEditInput
            v-model="localPerson.maiden_name"
            :label="$t('attributes.maiden_name')"
            name="first_name"
            edition-mode
          />
        </div>
        <div>
          <BaseShowEditInput
            v-model="localPerson.usual_name"
            :label="$t('attributes.usual_first_name')"
            :placeholder="$t('attributes.usual_first_name')"
            name="usual_first_name"
            edition-mode
          />
        </div>
      </template>
    </div>
    <div>
      <h4 class="text-base font-bold">
        {{ $t('attributes.contact') }}
      </h4>
      <div class="grid grid-cols-2 gap-2">
        <div>
          <BaseShowEditPhoneInput
            v-model:country-value="localPerson.phone_country"
            v-model:number-value="localPerson.phone_field"
            edition-mode
            :label="$t('attributes.phone')"
            name="phone"
            :errors="errors"
          />
        </div>
        <div>
          <BaseShowEditPhoneInput
            v-model:country-value="localPerson.mobile_country"
            v-model:number-value="localPerson.mobile_field"
            edition-mode
            :label="$t('attributes.mobile')"
            name="mobile"
            :errors="errors"
          />
        </div>
        <div>
          <BaseShowEditInput
            v-model="localPerson.email"
            :label="$t('attributes.email')"
            name="email"
            edition-mode
          />
        </div>
        <div class="col-span-2 col-start-1">
          <h4 class="text-base font-bold">
            {{ $t('attributes.document_delivery_preference') }}
          </h4>
          <SelectDocumentDeliveryPreference
            v-model="localPerson.document_delivery_preference"
            size="small"
            edition-mode
          />
        </div>
      </div>
    </div>

    <div
      v-if="
        fiscalCategories &&
        localPerson.invoiceable_company?.length > 0 &&
        localPerson.invoiceable_company &&
        metadata?.models?.companies
      "
    >
      <h4 class="text-base font-bold">
        {{ $t('attributes.fiscal_category') }}
      </h4>
      <div
        class="grid grid-cols-2 rounded border border-gray-100 bg-gray-50 p-2"
      >
        <template
          v-for="fiscalCategory in localPerson.invoiceable_company"
          :key="fiscalCategory.id"
        >
          <SelectComponent
            v-model="fiscalCategory.fiscal_category"
            :label="
              metadata.models.companies.find(
                (company) => company.id === fiscalCategory.company_id
              )?.name
            "
            name="fiscal_category"
            :values="fiscalCategories"
            :errors="errors?.fiscal_category"
            edition-mode
          />
        </template>
      </div>
    </div>
    <div>
      <h4 class="text-base font-bold">
        {{ $t('attributes.address') }}
      </h4>
      <div class="w-2/3">
        <BaseShowEditAddress
          v-model:country="localPerson.country"
          v-model:city-id="localPerson.city_id"
          v-model:zip="localPerson.zip"
          v-model:city="localPerson.city"
          v-model:extra-street="localPerson.extra_street"
          v-model:street="localPerson.street"
          v-model:current-city-object="localPerson.city_object"
          class="grid-cols-2"
          edition-mode
          :errors="errors"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import BaseShowEditRadio from '@c/addf-package/components/BaseShowEditRadioGroup/BaseShowEditRadio.vue'
import SelectCivility from '@c/InitialStateFormComponent/Enums/SelectCivility.vue'
import { mapGetters } from 'vuex'
import Gender from '@/assets/enums/person/Gender'
import BaseShowEditAddress from '@c/BaseShowEditAddress.vue'
import BaseShowEditPhoneInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditPhoneInput.vue'
import WalkmanButton from '@c/WalkmanButton.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import SelectDocumentDeliveryPreference from '@c/InitialStateFormComponent/Enums/SelectDocumentDeliveryPreference.vue'
import { Country } from '@/assets/enums/index.js'

export default {
  name: 'ShowPersonModalEdit',
  components: {
    SelectDocumentDeliveryPreference,
    SelectComponent,
    WalkmanButton,
    BaseShowEditPhoneInput,
    BaseShowEditAddress,
    SelectCivility,
    BaseShowEditRadio,
    BaseShowEditInput
  },
  props: {
    person: {
      type: Object,
      required: true
    }
  },
  emits: ['update:person'],
  data() {
    return {}
  },
  computed: {
    Country() {
      return Country
    },
    ...mapGetters({
      metadata: 'metadata/getInitialMetadata',
      fiscalCategories: 'metadata/getFiscalCategoriesMetadata',
      errors: 'auth/getErrors'
    }),
    Gender() {
      return Gender
    },
    localPerson: {
      get() {
        return this.person
      },
      set(value) {
        this.$emit('update:person', value)
      }
    }
  },
  watch: {
    'localPerson.country'(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (
          !this.localPerson.phone_field ||
          this.localPerson.phone_field.length === 0
        ) {
          this.localPerson.phone_country = newValue
        }
        if (
          !this.localPerson.mobile_field ||
          this.localPerson.mobile_field.length === 0
        ) {
          this.localPerson.mobile_country = newValue
        }
      }
    }
  }
}
</script>
